import * as React from 'react'
import { Bot as RobotIcon } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  useGetPlantRoboticsTreeQuery,
  useGetPlantRobotNodeDetailQuery
} from '@axilon-apps/domains-ontology-ui-state'
import { Empty } from '../../components/primitives/Empty'
import { plantRoboticsIndexHref } from '../../lib/plant-links'
import { RoboticsDetailPanel } from './RoboticsDetailPanel'
import { RoboticsTreeNav } from './RoboticsTreeNav'

const RoboticsPage = (): React.ReactElement => {
  const [searchParams] = useSearchParams()
  const selectedNodeId = searchParams.get('node')

  const tree = useGetPlantRoboticsTreeQuery()
  const nodes = tree.data?.nodes ?? []
  const detail = useGetPlantRobotNodeDetailQuery(selectedNodeId ?? '', {
    skip: !selectedNodeId
  })

  const breadcrumbName = React.useMemo(() => {
    if (!detail.data) return selectedNodeId ?? ''
    switch (detail.data.kind) {
      case 'RobotController':
        return detail.data.controller.display_name ?? detail.data.controller.name
      case 'RobotArm':
        return detail.data.arm.name
      case 'RobotJob':
        return detail.data.job.name
      case 'RobotIOSignal':
        return detail.data.signal.name
      case 'RobotCondition':
        return detail.data.condition.name
      case 'VisionProcess':
        return detail.data.process.name
      case 'VisionData':
        return detail.data.data.name
    }
  }, [detail.data, selectedNodeId])

  return (
    <div className="flex flex-1 flex-col gap-card">
      {selectedNodeId ? (
        <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
          <Link to={plantRoboticsIndexHref()} className="hover:text-ink hover:underline">
            Robotics
          </Link>
          <span className="mx-1.5 text-ink-4">/</span>
          <span className="text-ink">{breadcrumbName}</span>
        </nav>
      ) : (
        <header>
          <h1 className="m-0 text-2xl font-semibold text-ink">Robotics</h1>
          <p className="mt-1 text-sm text-ink-3">{roboticsSubtitle(tree.data)}</p>
        </header>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[18.75rem_minmax(0,1fr)]">
        <RoboticsTreeNav
          nodes={nodes}
          isLoading={tree.isLoading}
          selectedId={selectedNodeId ?? undefined}
        />
        <div className="flex min-w-0 flex-col gap-card">
          {selectedNodeId ? (
            <RoboticsDetailPanel nodeId={selectedNodeId} />
          ) : (
            <div className="flex min-h-80 flex-col rounded-xl border bg-card text-card-foreground shadow-sm">
              {tree.isError ? (
                <Empty
                  title="Couldn't load Robotics tree"
                  hint="Check the proxy is reachable and try again."
                  icon={RobotIcon}
                />
              ) : (
                <Empty
                  title="Select a robot node"
                  hint="Pick a controller, arm, or job to see jobs, I/O signals, conditions, and call graph links."
                  icon={RobotIcon}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function roboticsSubtitle(
  tree:
    | {
        totalControllerCount: number
        totalArmCount: number
        totalJobCount: number
      }
    | undefined
): string {
  if (!tree) {
    return 'Robot controllers, arms, jobs, I/O signals, and welding/work conditions.'
  }
  return [
    `${tree.totalControllerCount.toLocaleString()} ${tree.totalControllerCount === 1 ? 'controller' : 'controllers'}`,
    `${tree.totalArmCount.toLocaleString()} ${tree.totalArmCount === 1 ? 'arm' : 'arms'}`,
    `${tree.totalJobCount.toLocaleString()} ${tree.totalJobCount === 1 ? 'job' : 'jobs'}`
  ].join(' · ')
}

export default RoboticsPage
